/**
 * Service detail ("external service") field helpers shared between the create
 * form and its tests. Kept free of React/component imports so the pure
 * transformation can be unit-tested under the node-env vitest config.
 */

import type { ComplianceTaskDefinitionResponseDtoExternalServiceConfig } from "@/generated/model";

/** Field as held in the create form's state — `name` is derived on save. */
export interface ExternalServiceFormField {
  name?: string;
  label: string;
  type: string;
  required: boolean;
}

/** Field as persisted in `externalServiceConfig.fields` and read back by the
 * completion form, which keys a contractor's answers by `name`. */
export interface ExternalServiceField {
  name: string;
  label: string;
  type: string;
  required: boolean;
}

export const EXTERNAL_SERVICE_FIELD_TYPES = [
  "text",
  "number",
  "date",
  "yes_no",
] as const;

export type ExternalServiceFieldType =
  (typeof EXTERNAL_SERVICE_FIELD_TYPES)[number];

/**
 * Validate a single completion answer against its field type. Returns an error
 * message to display, or null when the answer is acceptable. Mirrors the backend
 * `ExternalServiceConfig.validateAnswers` rules so the UI can fail fast and gate
 * submission with the same logic the API enforces.
 */
export function validateExternalServiceAnswer(
  type: string,
  value: unknown,
  required: boolean,
): string | null {
  if (isEmptyAnswer(value)) {
    return required ? "This field is required" : null;
  }
  if (type === "number" && !isNumeric(value)) {
    return "Enter a valid number";
  }
  if (type === "date" && !isIsoDate(value)) {
    return "Enter a valid date";
  }
  if (type === "yes_no" && value !== "yes" && value !== "no") {
    return 'Select "Yes" or "No"';
  }
  return null;
}

function isEmptyAnswer(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  return typeof value === "string" && value.trim() === "";
}

function isNumeric(value: unknown): boolean {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (typeof value !== "string" || value.trim() === "") {
    return false;
  }
  return Number.isFinite(Number(value));
}

function isIsoDate(value: unknown): boolean {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return value === date.toISOString().slice(0, 10);
}

function slugify(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Defensively parse the externalServiceConfig DTO. The orval-generated type is
 * opaque (`Record<string, unknown>`) but the BE shape is `{ fields: [{...}] }`.
 * Fields persisted before a type was captured default to "text".
 */
export function parseExternalServiceFields(
  config: ComplianceTaskDefinitionResponseDtoExternalServiceConfig | undefined,
): ExternalServiceField[] {
  if (!config || typeof config !== "object") return [];
  const maybeFields = (config as { fields?: unknown }).fields;
  if (!Array.isArray(maybeFields)) return [];
  return maybeFields
    .filter(
      (f): f is ExternalServiceField =>
        !!f &&
        typeof f === "object" &&
        typeof (f as ExternalServiceField).name === "string" &&
        typeof (f as ExternalServiceField).label === "string",
    )
    .map((f) => ({
      name: f.name,
      label: f.label,
      type: typeof f.type === "string" ? f.type : "text",
      required: !!f.required,
    }));
}

/**
 * Assign every service detail field a stable, unique `name`. The completion
 * form keys answers by `name`, so a field's name must be stable for the life
 * of the definition and unique within it. Names are derived from the label
 * (slugified) and any existing name is preserved on edit; empty labels and
 * collisions fall back to a positional key.
 */
export function buildExternalServiceFields(
  fields: ExternalServiceFormField[],
): ExternalServiceField[] {
  const used = new Set<string>();
  return fields.map((field, index) => {
    let name =
      field.name?.trim() || slugify(field.label) || `field_${index + 1}`;
    while (used.has(name)) {
      name = `${name}_${index + 1}`;
    }
    used.add(name);
    return {
      name,
      label: field.label,
      type: field.type,
      required: field.required,
    };
  });
}
